import { Component, ElementRef, EventEmitter, Input, Output, ViewChild, AfterViewInit } from '@angular/core';
import {
  OrderedItem,
  OrderedItemService,
  OrderedSpecial,
  OrderSpecialService,
  UpsellType,
  OrderService,
  OrderableItem,
  ConfigService,
  StoreConfig,
} from 'ngx-web-api';
import { Subscription, of } from 'rxjs';
import { UiOrchestratorService } from '../../core/services/ui-orchestrator.service';
import { SpecialWizard } from '../../domain/special-wizard';
import { SpecialWizardStep } from '../../domain/special-wizard-step';
import { SpecialWizardService } from '../../core/services/special-wizard.service';
import { ViewportService } from '../../core/services/viewport.service';
import { distinctUntilChanged, mergeMap, catchError } from 'rxjs/operators';
import { ViewPort } from '../../domain/view-port.model';
import { BreakpointService } from '../../core/services/breakpoint.service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { HighlightElementsService } from '../highlight-elements.service';
import { GTMService } from '../../core/services/gtm.service';
import { EcommerceTriggeringArea } from '../../domain/ecommerce-triggering-area.enum';
import { EcommerceTriggeringPoint } from '../../domain/ecommerce-triggering-point.enum';
import { FocusService } from 'app/focus.service';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'fts-special-wizard-modal-content',
  templateUrl: './special-wizard-modal-content.component.html',
  styleUrls: ['./special-wizard-modal-content.component.scss'],
})
export class SpecialWizardModalContentComponent implements AfterViewInit {
  @Input()
  specialWizard: SpecialWizard;
  @Input()
  isUpsell: boolean;
  @Input()
  upsellType: UpsellType;
  @Input()
  featuredContentName: string;
  @Input()
  featuredAreaName: string;
  @Output()
  hideModal = new EventEmitter();
  storeConfig?: StoreConfig;

  @ViewChild('scrollWrapper', { static: true })
  private scrollWrapper: ElementRef;
  @ViewChild('modalBody', { static: true })
  private modalBody: ElementRef;

  specialAdditionFailedMode: boolean;
  addingSpecialToOrder: Subscription;
  specialWizardBodySet: boolean = true;
  isMobile: boolean;

  constructor(
    private orderSpecialService: OrderSpecialService,
    public orderedItemService: OrderedItemService,
    public uiService: UiOrchestratorService,
    private viewportService: ViewportService,
    private specialWizardService: SpecialWizardService,
    public breakPointService: BreakpointService,
    private orderService: OrderService,
    public activeModal: NgbActiveModal,
    private highlightService: HighlightElementsService,
    private gtmService: GTMService,
    private focusService: FocusService,
    private liveAnnouncer: LiveAnnouncer,
    private translateService: TranslateService,
    private configService: ConfigService
  ) {}

  ngAfterViewInit(): void {
    const currentViewport = this.viewportService.currentViewport;
    this.isMobile = currentViewport.isMobile;
    if (currentViewport.isMobile) {
      this.specialWizardService.setSpecialWizardBody(this.modalBody);
    } else {
      this.specialWizardService.setSpecialWizardBody(this.scrollWrapper);
    }

    this.viewportService.viewport$.pipe(distinctUntilChanged()).subscribe((viewport: ViewPort) => {
      this.isMobile = viewport.isMobile;
      if (viewport.isMobile) {
        this.specialWizardService.setSpecialWizardBody(this.modalBody);
      } else {
        this.specialWizardService.setSpecialWizardBody(this.scrollWrapper);
      }
    });

    setTimeout(() => this.focusService.activateFocusTrap('specialWizardModalContent', 'cancelButton'));
    this.configService.storeConfig$.subscribe(config => {
      this.storeConfig = config;
    });
  }

  addSpecialToOrder() {
    if (this.storeConfig?.hasCateringNoticeTime) {
    }
    const specialOrderedItems: (OrderedItem & { sourceStepId: string })[] = this.specialWizard.steps.map(step => {
      const item = step.selectedOrderedItem.copy();
      if (item) {
        item.sourceStepId = step.stepIndex;
        return item;
      }
    });
    const specialOrdererableItems: (OrderableItem & { sourceStepId: string })[] = this.specialWizard.steps.map(step => {
      const item = step.selectedOrderableItem.copy();
      if (item) {
        item.sourceStepId = step.stepIndex;
        return item;
      }
    });

    if (this.storeConfig?.hasCateringNoticeTime) {
      specialOrderedItems.forEach(item => {
        const matchedOrderable = specialOrdererableItems.find(
          orderable => orderable.name === item.item && orderable?.sourceStepId === item?.sourceStepId
        );
        if (matchedOrderable) {
          const sizePrice = matchedOrderable.getSizePrice(item.size);
          item.noticePeriod = sizePrice ? sizePrice.noticePeriod : 0;
        }
      });
    }

    const special = new OrderedSpecial();
    special.isUpsell = this.isUpsell;
    special.upsellType = this.upsellType;
    special.featuredAreaName = this.featuredAreaName;
    special.featuredContentName = this.featuredContentName;
    special.items = specialOrderedItems;

    special.special = this.specialWizard.orderableSpecial.name;
    special.couponCode = this.specialWizard.orderableSpecial.code;
    this.addingSpecialToOrder = this.orderSpecialService
      .addSpecialToOrder(special)
      .pipe(
        mergeMap(orderedSpecial => {
          if (orderedSpecial) {
            this.gtmService.pushAddToCartEvent({
              triggeringArea: EcommerceTriggeringArea.SPECIAL_WIZARD,
              triggeringPoint: EcommerceTriggeringPoint.ORDER_BUTTON,
              specialOrdinal: orderedSpecial.ordinal,
              specialName: orderedSpecial.special,
            });
          }
          if (this.specialWizard.removePending) {
            return this.orderService.removePendingDiscount().pipe(catchError(() => of(null)));
          }
          return of(null);
        })
      )
      .subscribe(
        () => {
          this.activeModal.close();
          this.liveAnnouncer.announce(
            this.translateService.instant('component.size_price_button.add_to_order_confrim_message', {
              printName: special.special,
            }),
            2000
          );
        },
        () => {
          this.scrollBodyToTop();
          this.specialAdditionFailedMode = true;
        }
      );
  }

  handleCancel() {
    this.activeModal.close();
  }

  goToNextStep() {
    this.scrollBodyToTop();
    setTimeout(() => {
      this.specialWizard.goToNextStep();
      this.focusService.applyFocus('item-editor-body');
    });
  }

  onItemSelected(isCustomizing?: boolean) {
    if (this.specialWizard.currentStep) {
      if (isCustomizing) {
        this.specialWizard.currentStep.shouldForceItemSelection = false;
        setTimeout(() => this.focusService.applyFocus('special-wizard-item-editor'), 500);
        this.scrollBodyToTop();
      } else if (!this.specialWizard.isComplete && !this.specialWizard.isLastStepSelected) {
        this.goToNextStep();
        this.specialWizard.currentStep.shouldForceItemSelection = false;
      } else {
        this.specialWizard.currentStep.shouldForceItemSelection = true;
      }
    }
  }

  onStepClick(step: SpecialWizardStep): void {
    if (step.stepIndex > this.specialWizard.currentStepIndex && !this.specialWizard.hasAllPreviousStepsFulfilled(step)) {
      this.highlightService.highLightDataFlashable(true);
      return;
    }
    setTimeout(() => {
      this.specialWizard.setCurrentStepIndex(step.stepIndex);
      this.resetSpecialAdditionFailedMode();

      if (this.specialWizard.currentStep) {
        this.specialWizard.currentStep.shouldForceItemSelection =
          !!this.specialWizard.currentStep.selectedOrderableItem && this.specialWizard.currentStep.canChangeItem;
      }
    });
  }

  resetSpecialAdditionFailedMode() {
    this.specialAdditionFailedMode = false;
  }

  get shouldForceAddToOrder() {
    if (this.specialWizard.currentStep) {
      return (
        this.specialWizard.currentStep.shouldForceItemSelection && this.specialWizard.isComplete && this.specialWizard.isLastStepSelected
      );
    }

    return false;
  }

  private scrollBodyToTop() {
    if (this.isMobile && this.modalBody) {
      this.modalBody.nativeElement.scrollTop = 0;
    } else {
      setTimeout(
        () => this.scrollWrapper.nativeElement.scrollTo({ top: this.scrollWrapper.nativeElement.offsetTop, left: 0, behavior: 'smooth' }),
        100
      );
    }
  }
}
